import { parse } from "date-fns";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from "../utils";
import {
  calculateLeadValue,
  formatCurrency,
  formatLeadTypeToIcon,
  formatLossReason,
  formatPhoneNumber,
  getCallOutcomeDisplay,
  LEAD_CATEGORIES,
  LEAD_SOURCES,
} from "../utils/displayFormatters";
import { isPastDue } from "../utils/leadUtils";

const ICON = {
  user: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  phone:
    "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  mail: "M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  calendar:
    "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  shield:
    "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  source:
    "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z",
  location:
    "M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z",
  pencil:
    "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  copy: "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z",
  close: "M6 18L18 6M6 6l12 12",
  check: "M5 13l4 4L19 7",
  chevronDown: "M19 9l-7 7-7-7",
  alert: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
};

const Icon = ({ d, className = "h-3.5 w-3.5" }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d={d}
    />
  </svg>
);

const INPUT =
  "w-full bg-white border border-primary rounded px-2 py-0.5 text-sm text-text-primary focus:ring-2 focus:ring-primary/25 focus:outline-none";
const PENCIL =
  "h-3.5 w-3.5 text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity";
const ICON_BTN =
  "text-text-secondary hover:text-primary p-0.5 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all";
// An empty field is an action, so it gets the action color and says what
// clicking does. Grey placeholder text here reads as "disabled" — exactly
// backwards for the field you most want someone to fill in.
const EMPTY_VALUE =
  "text-sm font-medium text-primary hover:text-primary-dark transition-colors";

// The name sits at the top of the identity band, so it gets its own input
// rather than INPUT with a font-size class layered on top of INPUT's own.
const INPUT_LG =
  "w-full max-w-md bg-white border border-primary rounded px-2 py-1 text-lg font-semibold text-text-primary focus:ring-2 focus:ring-primary/25 focus:outline-none";

const SavedTag = () => (
  <span className="flex flex-shrink-0 items-center gap-0.5 text-xs text-status-converted">
    <Icon d={ICON.check} className="h-3 w-3" />
    Saved
  </span>
);

/**
 * One shell for every editable field in the hero.
 *
 * Filled and empty cells get the same chrome — none at rest, a soft tint on
 * hover — because the box isn't what distinguishes them. Their *jobs* differ:
 * a filled cell is there to be read, so anything drawn around the value is
 * noise; an empty cell has nothing to read and exists only to prompt, so it
 * carries a "+ Add …" in the action color. The label is what marks either one
 * as a field, which is the convention every form has already taught.
 *
 * Hover-reveal is safe here: CLAUDE.md pins this app to 1920x1080 and 1366x768
 * desktop, so there is always a pointer.
 *
 * Pass `asDiv` when the cell holds its own buttons or a select, so we don't
 * nest interactive elements inside the shell button.
 */
const Field = ({
  label,
  icon,
  editing,
  onEdit,
  actions,
  saved,
  asDiv = false,
  children,
}) => {
  const clickable = !!onEdit && !editing;
  const Tag = clickable && !asDiv ? "button" : "div";
  const state = editing
    ? "border-primary bg-white"
    : "border-transparent hover:bg-accent-teal-light-bg";

  return (
    <Tag
      {...(Tag === "button" ? { type: "button" } : {})}
      {...(clickable ? { onClick: onEdit } : {})}
      className={`group block w-full h-full text-left rounded-lg border px-2 py-1.5 transition-colors ${
        clickable ? "cursor-pointer" : ""
      } ${state}`}
    >
      <div className="flex items-center gap-1.5">
        <Icon d={icon} className="h-3 w-3 flex-shrink-0 text-slate-400" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-secondary">
          {label}
        </span>
      </div>
      <div className="mt-0.5 flex min-h-[20px] items-center justify-between gap-2">
        <div className="min-w-0 flex-1">{children}</div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          {saved ? <SavedTag /> : actions}
        </div>
      </div>
    </Tag>
  );
};

const LeadHeroSection = ({
  currentLead,
  isSideCart,
  nameOrPhone,
  editingField,
  editValue,
  locations = [],
  leadServices,
  followUpDismissed,
  hasInteracted,
  isOpen,
  setIsOpen,
  startEditing,
  handleEditChange,
  cancelEditing,
  saveEdit,
  handleDismissFollowUp,
  appointmentCount,
  messageCount,
  noteCount,
  callRecordingCount,
  currentCategory = "prospect",
  onCategoryChange,
  // "header" draws name, phone and email; "details" draws the labelled
  // fields. Each view places both parts itself.
  part,
}) => {
  const [savedFields, setSavedFields] = useState({});

  // Flash "Saved" in the field's action slot. Saving clears editingField, so
  // the confirmation has to live in the display state or it never renders.
  const flashSaved = (fieldName) => {
    setSavedFields((prev) => ({ ...prev, [fieldName]: true }));
    setTimeout(() => {
      setSavedFields((prev) => ({ ...prev, [fieldName]: false }));
    }, 2000);
  };

  const handleBlur = async (fieldName, valueToSave = null) => {
    if (editingField === fieldName) {
      try {
        await saveEdit(fieldName, valueToSave);
        flashSaved(fieldName);
      } catch (error) {
        console.error(`Error saving ${fieldName}:`, error);
      }
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === "Enter") {
      handleBlur(fieldName);
    }
    if (e.key === "Escape") {
      cancelEditing();
    }
  };

  // Outcome of the forwarded phone call (Missed call / Message left / Answered /
  // …), shown next to the type icon for phone leads. Same formatter as the list.
  const callOutcome = getCallOutcomeDisplay(currentLead);

  // The locations list arrives from its own fetch, so it is empty on first
  // paint and stays empty if that call fails. Keep the lead's own location in
  // the list either way, or the field would render blank and lose the name.
  const locationOptions =
    currentLead.location &&
    !locations.some((l) => l.id === currentLead.location.id)
      ? [currentLead.location, ...locations]
      : locations;

  const editingFollowUp = editingField === "follow_up_date";

  // The practice's timezone, from the lead's location or the locations list.
  // Without one the Past due chip stays off rather than guess a day.
  const practiceTimezone =
    currentLead.location?.effective_timezone ||
    locations.find((l) => l.id === currentLead.location?.id)?.effective_timezone;
  const reminderPastDue = isPastDue(currentLead.follow_up_date, practiceTimezone);

  // In the sidecart the header part is a row of the shared header card, so it
  // draws no box of its own; the Patient card is roomier. The full page keeps
  // the original box.
  const heroBox =
    isSideCart && part === "header"
      ? "relative overflow-visible"
      : isSideCart && part === "details"
        ? "px-[18px] py-4 rounded-md border border-border bg-white relative overflow-visible"
        : "px-4 py-3 rounded-md mb-3 border border-border bg-white relative overflow-visible";

  // Shared hero content render function
  const renderHeroContent = () => (
    <div
      data-testid={isSideCart && part === "details" ? "sidecart-patient-card" : undefined}
      className={heroBox}
    >
      {isSideCart && part === "details" && (
        <h3 className="mb-3 text-sm font-semibold text-text-primary">Patient</h3>
      )}
      {part !== "details" && (
      <>
      {/* Identity band — who this is, then how they stand. The status
          cluster sits in the flow on the right, so the name no longer has to
          dodge an absolutely-positioned box behind a hard-coded column cap. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editingField === "name" ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => handleEditChange(e.target.value)}
              onBlur={() => handleBlur("name")}
              onKeyDown={(e) => handleKeyDown(e, "name")}
              className={INPUT_LG}
              placeholder="Enter name"
              autoFocus
            />
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  startEditing(
                    "name",
                    `${currentLead.first_name || ""} ${currentLead.last_name || ""}`.trim(),
                  )
                }
                className="group flex min-w-0 items-center gap-1.5 rounded text-left"
                title="Edit name"
              >
                <span
                  className={`ph-mask truncate text-lg font-semibold leading-tight ${
                    nameOrPhone ? "text-text-primary" : "text-primary"
                  }`}
                >
                  {nameOrPhone || "+ Add name"}
                </span>
                {nameOrPhone && <Icon d={ICON.pencil} className={PENCIL} />}
              </button>
              {savedFields.name && <SavedTag />}
            </div>
          )}

          {/* Phone and email read as themselves, so they carry no label.
              The value stays the action — call, mail — and hover gives the
              secondary ones. */}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            {editingField === "phone" ? (
              <input
                type="text"
                value={formatPhoneNumber(editValue)}
                onChange={(e) => handleEditChange(e.target.value)}
                onBlur={() => handleBlur("phone")}
                onKeyDown={(e) => handleKeyDown(e, "phone")}
                className={`${INPUT} max-w-[200px]`}
                placeholder="Enter phone number"
                autoFocus
              />
            ) : currentLead.phone ? (
              <span className="group inline-flex items-center gap-1">
                <a
                  href={`tel:${currentLead.phone}`}
                  data-ph-no-autocapture=""
                  className="ph-mask text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  {formatPhoneNumber(currentLead.phone)}
                </a>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(currentLead.phone)}
                  className={ICON_BTN}
                  title="Copy phone number"
                >
                  <Icon d={ICON.copy} className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => startEditing("phone", currentLead.phone)}
                  className={ICON_BTN}
                  title="Edit phone"
                >
                  <Icon d={ICON.pencil} className="h-3.5 w-3.5" />
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => startEditing("phone", "")}
                className={EMPTY_VALUE}
              >
                + Add phone
              </button>
            )}
            {savedFields.phone && <SavedTag />}

            <span className="text-slate-300" aria-hidden="true">
              ·
            </span>

            {editingField === "email" ? (
              <input
                type="email"
                value={editValue}
                onChange={(e) => handleEditChange(e.target.value)}
                onBlur={() => handleBlur("email")}
                onKeyDown={(e) => handleKeyDown(e, "email")}
                className={`${INPUT} max-w-[240px]`}
                placeholder="Enter email address"
                autoFocus
              />
            ) : currentLead.email ? (
              <span className="group inline-flex min-w-0 items-center gap-1">
                <a
                  href={`mailto:${currentLead.email}`}
                  data-ph-no-autocapture=""
                  className="ph-mask truncate text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  {currentLead.email}
                </a>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(currentLead.email)}
                  className={ICON_BTN}
                  title="Copy email address"
                >
                  <Icon d={ICON.copy} className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => startEditing("email", currentLead.email)}
                  className={ICON_BTN}
                  title="Edit email"
                >
                  <Icon d={ICON.pencil} className="h-3.5 w-3.5" />
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => startEditing("email", "")}
                className={EMPTY_VALUE}
              >
                + Add email
              </button>
            )}
            {savedFields.email && <SavedTag />}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {callOutcome && (
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${callOutcome.className}`}
              title="Outcome of the forwarded phone call"
            >
              {callOutcome.label}
            </span>
          )}
          {currentLead.type && (
            <span className="text-text-secondary" title={`${currentLead.type} lead`}>
              {formatLeadTypeToIcon(currentLead.type)}
            </span>
          )}
        </div>
      </div>
      </>
      )}

      {part !== "header" && (
      <>
      {/* Attributes — the fields you set, split by a rule from the identity
          you read. Three across in the sidecart, which is a fixed 1000px, so
          five fields make two rows and none is cut off; two across in the
          full-page view, where the hero is a third of the window. */}
      <div
        className={`grid gap-x-3 ${
          isSideCart ? "grid-cols-3 gap-y-[18px]" : "grid-cols-2 gap-y-2.5"
        }`}
      >
        {/* Lead category — who the lead is to the practice, not a status.
            A labelled field like the others; Not Applicable opens the
            loss-reason modal through onCategoryChange. */}
        <div className="min-w-0">
          <Field label="Category" icon={ICON.user} asDiv>
            <select
              value={currentCategory}
              onChange={(e) => onCategoryChange?.(e.target.value)}
              aria-label="Lead category"
              className="w-full truncate border-0 bg-transparent p-0 text-sm font-medium text-text-primary focus:ring-0 focus:outline-none cursor-pointer"
            >
              {LEAD_CATEGORIES.map((cat) => (
                <option key={cat.key} value={cat.key}>
                  {cat.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Source */}
        <div className="min-w-0">
          <Field
            label="Source"
            icon={ICON.source}
            saved={savedFields.source}
            asDiv
          >
            {/* No "+" here: the native chevron already reads as "pick one",
                so the action color alone carries the empty state. */}
            <select
              value={currentLead.source || ""}
              onChange={async (e) => {
                try {
                  await saveEdit("source", e.target.value);
                  flashSaved("source");
                } catch (error) {
                  console.error("Error saving source:", error);
                }
              }}
              className={`w-full truncate border-0 bg-transparent p-0 text-sm font-medium focus:ring-0 focus:outline-none cursor-pointer ${
                currentLead.source ? "text-text-primary" : "text-primary"
              }`}
            >
              <option value="">
                {currentLead.source ? "Select new source" : "Select source"}
              </option>
              {LEAD_SOURCES.map((source) => (
                <option key={source.key} value={source.key}>
                  {source.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Location — same select treatment as Source. A lead always belongs
            to a location, so there is no "unassigned" option to pick. */}
        <div className="min-w-0">
          <Field
            label="Location"
            icon={ICON.location}
            saved={savedFields.location}
            asDiv
          >
            <select
              value={currentLead.location?.id ?? ""}
              onChange={async (e) => {
                try {
                  await saveEdit("location", e.target.value);
                  flashSaved("location");
                } catch (error) {
                  console.error("Error saving location:", error);
                }
              }}
              className={`w-full truncate border-0 bg-transparent p-0 text-sm font-medium focus:ring-0 focus:outline-none cursor-pointer ${
                currentLead.location ? "text-text-primary" : "text-primary"
              }`}
            >
              {!currentLead.location && (
                <option value="">Select location</option>
              )}
              {locationOptions.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {/* Insurance */}
        <div className="min-w-0">
          <Field
            label="Insurance"
            icon={ICON.shield}
            editing={editingField === "insurance_provider"}
            saved={savedFields.insurance_provider}
            onEdit={() =>
              startEditing(
                "insurance_provider",
                currentLead.insurance_provider || "",
              )
            }
            actions={
              currentLead.insurance_provider ? (
                <Icon d={ICON.pencil} className={PENCIL} />
              ) : null
            }
          >
            {editingField === "insurance_provider" ? (
              <input
                type="text"
                value={editValue}
                onChange={(e) => handleEditChange(e.target.value)}
                onBlur={() => handleBlur("insurance_provider")}
                onKeyDown={(e) => handleKeyDown(e, "insurance_provider")}
                className={INPUT}
                placeholder="Insurance provider"
                autoFocus
              />
            ) : (
              <span
                className={`block truncate ${
                  currentLead.insurance_provider
                    ? "text-sm font-medium text-text-primary"
                    : EMPTY_VALUE
                }`}
              >
                {currentLead.insurance_provider || "+ Add insurance"}
              </span>
            )}
          </Field>
        </div>

        {/* Reminder */}
        <div className="min-w-0">
          <Field
            label="Reminder"
            icon={ICON.calendar}
            editing={editingFollowUp}
            saved={savedFields.follow_up_date}
            asDiv
            onEdit={() => {
              startEditing(
                "follow_up_date",
                currentLead.follow_up_date
                  ? parse(currentLead.follow_up_date, "yyyy-MM-dd", new Date())
                  : null,
              );
              setIsOpen(!isOpen);
            }}
            actions={
              currentLead.follow_up_date && !editingFollowUp ? (
                <>
                  <Icon d={ICON.pencil} className={PENCIL} />
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await saveEdit("follow_up_date", null);
                      } catch (error) {
                        console.error("Error clearing follow_up_date:", error);
                      }
                    }}
                    className={ICON_BTN}
                    title="Clear reminder"
                  >
                    <Icon d={ICON.close} className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : null
            }
          >
            {editingFollowUp ? (
              <DatePicker
                selected={editValue}
                onChange={(date) => {
                  handleEditChange(date);
                  setTimeout(async () => {
                    try {
                      await saveEdit("follow_up_date", date);
                      flashSaved("follow_up_date");
                    } catch (error) {
                      console.error("Error saving follow_up_date:", error);
                    }
                  }, 100);
                }}
                dateFormat="yyyy-MM-dd"
                className={INPUT}
                placeholderText="Select reminder date"
                isClearable
                autoFocus
              />
            ) : (
              <>
                {/* formatDate returns the string "N/A" for null, so it can't
                    be used as the falsy half of a fallback. */}
                <span
                  className={
                    currentLead.follow_up_date
                      ? "text-sm font-medium text-text-primary"
                      : EMPTY_VALUE
                  }
                >
                  {currentLead.follow_up_date
                    ? formatDate(currentLead.follow_up_date, "iso")
                    : "+ Add reminder"}
                </span>
                {isSideCart && reminderPastDue && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium text-red-700 bg-red-50 rounded-full">
                    Past due
                  </span>
                )}
                {!followUpDismissed &&
                  !hasInteracted &&
                  currentLead.follow_up_date && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium text-status-contacted bg-status-contacted/20 rounded-full">
                      💡 New
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismissFollowUp();
                        }}
                        className="ml-1 text-status-contacted hover:text-status-contacted/80 focus:outline-none"
                        aria-label="Dismiss notification"
                      >
                        ✕
                      </button>
                    </span>
                  )}
              </>
            )}
          </Field>
        </div>
      </div>

      {/* Loss Reason Alert */}
      {currentLead.loss_reason && (
        <div className="mt-2.5">
          <div className="flex items-start space-x-2.5 p-2.5 bg-status-no-show/10 border border-status-no-show/30 rounded-lg">
            <div className="bg-status-no-show/20 rounded-full p-1.5 flex-shrink-0">
              <Icon d={ICON.alert} className="h-3.5 w-3.5 text-status-no-show" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-status-no-show mb-0.5">
                {currentLead.lead_category === "not_applicable"
                  ? "Not Applicable"
                  : "Not Interested"}
              </div>
              <div className="text-sm text-status-no-show">
                {formatLossReason(currentLead.loss_reason)}
              </div>
              {currentLead.loss_reason_notes && (
                <p className="text-sm text-status-no-show/80 mt-1 break-words">
                  {currentLead.loss_reason_notes}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Estimated Lead Value */}
      {leadServices.length > 0 && false && (
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <span className="text-sm font-medium text-primary">
            Estimated Value:
          </span>
          <span className="text-xl font-bold text-status-converted">
            {formatCurrency(calculateLeadValue(leadServices, currentLead))}
          </span>
        </div>
      )}
      </>
      )}
      {/* Additional Details - Collapsed by default */}
      {/* <div className="pt-3 border-t">
        <LeadMoreDetails
          currentLead={currentLead}
          editingField={editingField}
          editValue={editValue}
          locations={locations}
          startEditing={startEditing}
          handleEditChange={handleEditChange}
          saveEdit={saveEdit}
        />
      </div> */}
    </div>
  ); // End of renderHeroContent

  return renderHeroContent();
};

export default LeadHeroSection;
